import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Settings, 
  Shield, 
  BarChart3,
  Users,
  Gamepad2,
  Trash2,
  LogOut,
  Server,
  CreditCard,
  Database,
  Package
} from 'lucide-react';
import { adminAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import AdminUserTable from '../components/admin/AdminUserTable';
import { AdminStatsCards, SubscriptionBreakdown, SystemHealth } from '../components/admin/AdminStatsCards';
import StripeSettings from '../components/admin/StripeSettings';
import ServerSubscriptionManagement from '../components/admin/ServerSubscriptionManagement';
import SubscriptionPlanManagement from '../components/admin/SubscriptionPlanManagement';
import GameRegistryManagement from '../components/admin/GameRegistryManagement';
import toast from 'react-hot-toast';

const AdminDashboard = () => {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    subscription: '',
    status: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  // System statistics
  const { data: systemStats, isLoading: statsLoading } = useQuery(
    'adminSystemStats',
    () => adminAPI.getSystemStats().then(res => res.data),
    { enabled: !!admin }
  );

  // Users data
  const { data: usersData, isLoading: usersLoading } = useQuery(
    ['adminUsers', searchTerm, filters],
    () => adminAPI.getUsers({
      search: searchTerm,
      ...filters
    }).then(res => res.data),
    { enabled: !!admin }
  );

  // Games data
  const { data: gamesData, isLoading: gamesLoading } = useQuery(
    ['adminGames', filters],
    () => adminAPI.getGames(filters).then(res => res.data),
    { enabled: !!admin }
  );

  // Settings data
  const { data: settingsData, isLoading: settingsLoading } = useQuery(
    'adminSettings',
    () => adminAPI.getSettings().then(res => res.data),
    { enabled: !!admin }
  );

  // Mutations (commented out for now to fix ESLint warnings)
  // const updateUserSubscriptionMutation = useMutation(
  //   ({ userId, subscriptionType, endDate }) => 
  //     adminAPI.updateUserSubscription(userId, { subscriptionType, endDate }),
  //   {
  //     onSuccess: () => {
  //       toast.success('User subscription updated successfully');
  //       queryClient.invalidateQueries('adminUsers');
  //     },
  //     onError: (error) => {
  //       toast.error(error.response?.data?.error || 'Failed to update subscription');
  //     }
  //   }
  // );

  if (!admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-discord-darkest">
        <div className="text-center">
          <Shield className="h-16 w-16 text-discord-red mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-discord-lightest mb-2">Access Denied</h1>
          <p className="text-discord-light">User authentication required.</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: BarChart3 },
    { id: 'users', name: 'Users', icon: Users },
    { id: 'games', name: 'Games', icon: Gamepad2 },
    { id: 'subscriptions', name: 'Server Subscriptions', icon: Server },
    { id: 'plans', name: 'Subscription Plans', icon: CreditCard },
    { id: 'stripe', name: 'Stripe Settings', icon: Shield },
    { id: 'settings', name: 'Settings', icon: Settings }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-discord-lightest mb-2">
              🛡️ Admin Dashboard
            </h1>
            <p className="text-discord-light">
              Manage your Discord Game Bot SaaS platform
            </p>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Shield className="h-8 w-8 text-discord-yellow" />
              <div className="text-right">
                <p className="text-sm font-medium text-discord-lightest">
                  {admin?.username}
                </p>
                <p className="text-xs text-discord-light">
                  Admin
                </p>
              </div>
            </div>
            
            <button
              onClick={() => {
                logout();
                navigate('/');
              }}
              className="btn btn-secondary flex items-center space-x-2"
              title="Logout"
            >
              <LogOut className="h-4 w-4" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="card">
        <div className="flex space-x-1 bg-discord-dark rounded-lg p-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                  activeTab === tab.id
                    ? 'bg-discord-blurple text-white'
                    : 'text-discord-light hover:text-discord-lightest hover:bg-discord-dark/50'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          <AdminStatsCards systemStats={systemStats} isLoading={statsLoading} />
          <SubscriptionBreakdown subscriptionStats={systemStats?.users?.subscriptionBreakdown} />
          <SystemHealth systemStats={systemStats} />
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-6">
          {usersLoading ? (
            <LoadingSpinner size="xl" />
          ) : (
            <AdminUserTable
              users={usersData?.users || []}
              pagination={usersData?.pagination}
              onSearch={setSearchTerm}
              onFilter={(key, value) => setFilters({ ...filters, [key]: value })}
              filters={{ ...filters, search: searchTerm }}
            />
          )}
        </div>
      )}

      {/* Games Tab */}
      {activeTab === 'games' && (
        <GameRegistryManagement />
      )}

      {/* Server Subscriptions Tab */}
      {activeTab === 'subscriptions' && (
        <div className="space-y-6">
          <ServerSubscriptionManagement />
        </div>
      )}

      {/* Subscription Plans Tab */}
      {activeTab === 'plans' && (
        <div className="space-y-6">
          <SubscriptionPlanManagement />
        </div>
      )}

      {/* Stripe Settings Tab */}
      {activeTab === 'stripe' && (
        <div className="space-y-6">
          <StripeSettings />
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          {settingsLoading ? (
            <LoadingSpinner size="xl" />
          ) : (
            <div className="card">
              <h2 className="text-xl font-bold text-discord-lightest mb-4">System Settings</h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-discord-light mb-2">
                    Max Free Games Per Day
                  </label>
                  <input
                    type="number"
                    defaultValue={settingsData?.settings?.maxFreeGamesPerDay || 10}
                    className="input w-32"
                    onChange={(e) => {
                      // Update setting
                    }}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-discord-light mb-2">
                      Premium Price ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      defaultValue={(settingsData?.settings?.premiumPrice || 499) / 100}
                      className="input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-discord-light mb-2">
                      Pro Price ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      defaultValue={(settingsData?.settings?.proPrice || 999) / 100}
                      className="input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-discord-light mb-2">
                      Server Price ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      defaultValue={(settingsData?.settings?.serverPrice || 1999) / 100}
                      className="input"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      defaultChecked={settingsData?.settings?.maintenanceMode || false}
                      className="rounded"
                    />
                    <span className="text-discord-light">Maintenance Mode</span>
                  </label>
                  
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      defaultChecked={settingsData?.settings?.newUserRegistration !== false}
                      className="rounded"
                    />
                    <span className="text-discord-light">Allow New User Registration</span>
                  </label>
                </div>

                <button
                  onClick={() => {
                    // Save settings
                    toast.success('Settings saved successfully');
                  }}
                  className="btn btn-primary"
                >
                  Save Settings
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
